import type { Plan, Ridden } from '@painpal/contracts';
import { addDays } from '@painpal/core';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { usePlan } from '../../api/queries';
import { formatHoursMinutes, formatWeekdayDayMonth, formatWhole } from '../../lib/format';
import { useIsPhone } from '../../ui/useMediaQuery';
import { PlanTag } from '../calendar/PlanTag';
import { formatEstimate, nextLine, planDuration, todayPlanView, type NextBlock, type TodayPlanView } from '../calendar/planModel';
import styles from './todaysPlan.module.css';

const Rule = ({ tall = false }: { tall?: boolean }) => <span className={tall ? styles.tallRule : styles.rule} aria-hidden="true" />;
const CalendarLink = () => <Link to="/calendar" className={styles.calendarLink}>CALENDAR ›</Link>;

function StripEyebrow({ today, done }: { today: string; done: boolean }) {
  return (
    <p className={styles.eyebrow}>
      TODAY&apos;S PLAN <span className={styles.date}>· {formatWeekdayDayMonth(today)}</span>
      {done && <span className={styles.badge}><span className={styles.dot} aria-hidden="true" />DONE</span>}
    </p>
  );
}

function Block({ plan, note, children }: { plan: Plan; note: boolean; children?: ReactNode }) {
  const duration = planDuration(plan);
  return (
    <div className={styles.block}>
      <PlanTag type={plan.type} size="strip" />
      {duration !== null && <span className={styles.duration}>{duration.text}{duration.defaulted && <span className={styles.defaulted}> (DEFAULT)</span>}</span>}
      {note && plan.note !== null && <><Rule /><span className={styles.note}>{plan.note}</span></>}
      {children}
    </div>
  );
}

function Estimate({ load, quiet }: { load: number; quiet: boolean }) {
  return <p className={styles.estimate} data-quiet={quiet || undefined}><span className={styles.figure}>{formatEstimate(load)}</span> <span className={styles.unit}>LOAD</span></p>;
}

function RiddenFigures({ ridden }: { ridden: Ridden }) {
  return (
    <div className={styles.ridden}>
      <span className={styles.label}>{ridden.ride === null ? 'RIDDEN' : `RIDDEN · ${ridden.ride.name.toUpperCase()}`}</span>
      <p className={styles.riddenValues}>
        <span className={styles.figure}>{formatHoursMinutes(ridden.movingS)}</span> <span className={styles.unit} data-part="time">TIME</span>
        <span className={styles.sep} aria-hidden="true">·</span> <span className={styles.figure}>{formatWhole(ridden.load)}</span> <span className={styles.unit}>LOAD</span>
      </p>
    </div>
  );
}

// The board's VIEW RIDE, to the day's first ride; the calendar when there's no ride to link.
function RideLink({ ridden }: { ridden: Ridden }) {
  if (ridden.ride === null) return <CalendarLink />;
  return <Link to="/activities/$activityId" params={{ activityId: ridden.ride.id }} className={styles.calendarLink}>VIEW RIDE ›</Link>;
}

function NextUp({ next, rest }: { next: NextBlock | null; rest: boolean }) {
  if (next === null) return null;
  return (
    <>
      {rest
        ? <p className={styles.nextLine}>NEXT · {nextLine(next)}</p>
        : <div className={styles.next}><span className={styles.label}>UP NEXT</span><span className={styles.nextText}>{nextLine(next)}</span></div>}
      <Rule tall />
    </>
  );
}

function NothingPlanned({ today }: { today: string }) {
  return (
    <>
      <div className={styles.left}>
        <StripEyebrow today={today} done={false} />
        <p className={styles.nothing}>Nothing planned for today.<span className={styles.label}>ADD A BLOCK, OR PLAN THE WHOLE WEEK</span></p>
      </div>
      <Link to="/calendar" className={styles.planWeek}>PLAN YOUR WEEK ›</Link>
    </>
  );
}

function DesktopStrip({ today, plan, ridden, next }: TodayPlanView & { today: string; plan: Plan }) {
  return (
    <>
      <div className={styles.left}>
        <StripEyebrow today={today} done={ridden !== null} />
        <Block plan={plan} note />
      </div>
      <div className={styles.right}>
        {ridden === null
          ? <><NextUp next={next} rest={plan.type === 'rest'} /><Estimate load={plan.estimatedLoad} quiet={plan.type === 'rest'} /></>
          : <><div className={styles.plannedQuiet}><span className={styles.label}>PLANNED</span><Estimate load={plan.estimatedLoad} quiet /></div><Rule tall /><RiddenFigures ridden={ridden} /></>}
        <Rule tall />
        {ridden === null ? <CalendarLink /> : <RideLink ridden={ridden} />}
      </div>
    </>
  );
}

function PhoneStrip({ today, plan, ridden, next }: TodayPlanView & { today: string; plan: Plan }) {
  return (
    <>
      <div className={styles.top}><StripEyebrow today={today} done={ridden !== null} /><CalendarLink /></div>
      <Block plan={plan} note={false}><span className={styles.spacer} /><Estimate load={plan.estimatedLoad} quiet={ridden !== null || plan.type === 'rest'} /></Block>
      {plan.note !== null && <p className={styles.note}>{plan.note}</p>}
      {ridden !== null && <RiddenFigures ridden={ridden} />}
      {plan.type === 'rest' && next !== null && <p className={styles.nextLine}>NEXT · {nextLine(next)}</p>}
    </>
  );
}

// Shown whenever the Fitness page is, rides or not. Pending, failed or holding another range's placeholder, it stays out of the way: nothing else depends on it.
// The read starts a day early because the profile zone's today, which the strip shows, can be a day either side of the browser's.
export function TodaysPlanStrip({ today: browserToday }: { today: string }) {
  const isPhone = useIsPhone();
  const read = usePlan(addDays(browserToday, -1), addDays(browserToday, 7));
  if (!read.isSuccess || read.isPlaceholderData) return null;
  const { today } = read.data;
  const view = todayPlanView(read.data.days, today);
  const Strip = isPhone ? PhoneStrip : DesktopStrip;
  return (
    <section className={styles.strip} aria-label="Today's plan">
      {view.plan === null ? <NothingPlanned today={today} /> : <Strip today={today} {...view} plan={view.plan} />}
    </section>
  );
}
